import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Alert,
  CircularProgress,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  Radio
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SchoolIcon from '@mui/icons-material/School';
import { Event, FormData, InstalmentInput, PaymentPlan } from '../types';
import { eventApi } from '../services/api';
import DynamicForm from './DynamicForm';
import InstalmentsDialog from './InstalmentsDialog';
import GroupParticipantsDialog, { GroupParticipant } from './GroupParticipantsDialog';
import LanguageSelector from './LanguageSelector';
import { useTranslation } from '../i18n/TranslationContext';
import { AppliedSupplement, computeSupplements, personTotal, signedMoney, sumSupplements, tipologiaAmount } from '../utils/supplements';
import { applyEventMeta } from '../tracking/meta';
import {
  setupTracking,
  trackEventView,
  trackBeginRegistration,
  trackConversion,
  storePendingConversion,
} from '../tracking/tracking';

const EventPage: React.FC = () => {
  const { slug, section } = useParams<{ slug: string; section?: string }>();
  const { t, lang, syncResolved, setForcedDefault, fmtMoney } = useTranslation();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<FormData | null>(null);
  const [registrationId, setRegistrationId] = useState<number | null>(null);
  const [bankDetails, setBankDetails] = useState<any>(null);
  const [bankDetailsDialogOpen, setBankDetailsDialogOpen] = useState(false);
  const [cartaDocenteDialogOpen, setCartaDocenteDialogOpen] = useState(false);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null);
  const [instalmentsDialogOpen, setInstalmentsDialogOpen] = useState(false);
  const [activePlan, setActivePlan] = useState<PaymentPlan | null>(null);
  const [activeTotal, setActiveTotal] = useState<number>(0);
  const [activeMethod, setActiveMethod] = useState<string>('');

  useEffect(() => {
    const fetchEvent = async () => {
      if (!slug) {
        setError(t('evento.slug_mancante', 'Slug evento mancante'));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');
        const eventData = await eventApi.getEvent(slug, section, lang);
        setEvent(eventData);

        // Multilingua: attiva solo se l'evento la abilita; altrimenti si
        // resta sulla lingua di default qualunque sia la preferenza salvata
        const anyData = eventData as any;
        setForcedDefault(!anyData.multilingual_enabled);
        if (anyData.multilingual_enabled && anyData.resolved_language) {
          syncResolved(anyData.resolved_language, anyData.available_languages);
        }

        // Ricordato per il link "Torna all'evento" della cookie policy
        try {
          sessionStorage.setItem('last_event_slug', slug);
        } catch {
          // storage non disponibile: la policy mostrerà solo "Torna indietro"
        }

        // SEO + tracciamento configurati per l'evento
        applyEventMeta(eventData.seo_meta);
        setupTracking(eventData.tracking_config ?? null, eventData.tracking_scripts_list ?? []);
        trackEventView(eventData.name);
      } catch (err: any) {
        console.error('Error fetching event:', err);
        setError(err.response?.data?.error || t('evento.err_caricamento', "Errore nel caricamento dell'evento"));
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, section, lang]);

  const groupSizeOf = (fd: FormData | null | undefined): number => {
    const n = Number(fd?.group_size ?? 1);
    return Number.isFinite(n) && n > 1 ? n : 1;
  };
  const isGroupPending = () => groupSizeOf(pendingFormData) > 1;

  const proceedAfterForm = (formData: FormData) => {
    // Gratuita solo se non c'è nulla da pagare: una tipologia gratuita con
    // supplementi scelti passa dal pagamento per i soli supplementi
    if (computeOrder(formData).total <= 0) {
      setConfirmDialogOpen(true);
    } else {
      setSelectedPaymentMethod(null);
      setPaymentDialogOpen(true);
    }
  };

  const handleFormSubmit = async (formData: FormData) => {
    if (!slug || !event) return;

    // Check if selected price is free or paid
    const selectedPrice = event.prices?.find(p => p.id === formData.price_id);
    
    if (selectedPrice) {
      setPendingFormData(formData);

      // Iscrizione di gruppo: prima i dati dei partecipanti, poi il pagamento.
      // I partecipanti già inseriti in un tentativo precedente vengono preservati.
      if (groupSizeOf(formData) > 1) {
        const previous = pendingFormData?.participants;
        if (previous?.length) {
          setPendingFormData({ ...formData, participants: previous, referente_participates: pendingFormData?.referente_participates });
        }
        setGroupDialogOpen(true);
        return;
      }

      proceedAfterForm(formData);
    } else {
      // No price selected or no prices - proceed with submission
      await submitForm(formData);
    }
  };

  const handleGroupParticipantsConfirm = (participants: GroupParticipant[], referenteParticipates: boolean) => {
    if (!pendingFormData) return;
    const merged = { ...pendingFormData, participants, referente_participates: referenteParticipates };
    setPendingFormData(merged);
    setGroupDialogOpen(false);
    proceedAfterForm(merged);
  };

  const findActivePlan = (priceId: number | undefined, method: string): PaymentPlan | null => {
    if (!priceId || !event?.payment_plans) return null;
    return event.payment_plans.find(p => p.price_id === priceId && p.payment_method === method && !!p.enabled) || null;
  };

  const computeTotalForPrice = (priceId: number | undefined): number => tipologiaAmount(event, priceId);

  // Ordine completo, stesso calcolo del server: quota tipologia per ogni persona
  // che partecipa + supplementi del referente + supplementi di ogni partecipante
  const computeOrder = (fd: FormData | null | undefined) => {
    const unit = computeTotalForPrice(fd?.price_id as number | undefined);
    const isGroup = groupSizeOf(fd) > 1;
    const participants = isGroup ? (fd?.participants ?? []) : [];
    const referenteParticipates = !(isGroup && fd?.referente_participates === false);
    const persons = participants.length + (referenteParticipates ? 1 : 0);
    // Le percentuali si applicano alla quota di ciascuno: il referente che non
    // partecipa ha quota 0, le sue variazioni fisse valgono comunque
    const referenteBase = referenteParticipates ? unit : 0;
    const referenteSupplements: AppliedSupplement[] = computeSupplements(event, (fd ?? {}) as any, fd?.is_ente === true, false, referenteBase);
    const participantSupplements = participants.map(p => computeSupplements(event, p.lookups ?? {}, false, true, unit));
    // Ogni persona: quota + variazioni, mai sotto zero
    const total = Math.round((personTotal(referenteBase, referenteSupplements)
      + participantSupplements.reduce((acc, list) => acc + personTotal(unit, list), 0)) * 100) / 100;
    const supplementsTotal = Math.round((total - unit * persons) * 100) / 100;
    return { unit, persons, referenteSupplements, participantSupplements, supplementsTotal, total };
  };


  // Carta del Docente: ammessa solo a prezzo pieno. Se sulla tipologia
  // selezionata è attivo uno sconto, il metodo non va proposto.
  const selectedPriceHasActiveDiscount = (): boolean => {
    const priceId = pendingFormData?.price_id as number | undefined;
    const price = event?.prices?.find(p => p.id === priceId);
    if (!price || price.gratis === 1) return false;
    return !!price.discounted_price
      && !!price.valid_start_date
      && new Date() < new Date(price.valid_start_date + 'T23:59:59');
  };

  const submitForm = async (formData: FormData, paymentMethod?: string) => {
    try {
      setSubmitting(true);
      setError('');
      
      let dataToSubmit = paymentMethod
        ? { ...formData, payment_method: paymentMethod }
        : formData;
      dataToSubmit = remapKeysToField(dataToSubmit);
      // Lingua scelta: il BE la salva sull'iscrizione (email successive in lingua)
      (dataToSubmit as any).lang = lang ?? undefined;

      const registrationResponse = await eventApi.submitForm(slug!, dataToSubmit);

      const total = computeOrder(formData).total;
      // Con le rate, online si paga solo la prima: è quello il valore della conversione
      const paidValue = formData.instalments?.length
        ? formData.instalments.find(i => i.sequence === 1)?.amount ?? total
        : total;

      if ((paymentMethod === 'paypal' || paymentMethod === 'stripe' || paymentMethod === 'bonifico' || paymentMethod === 'carta_docente') && registrationResponse?.data?.subscription_id) {
        setRegistrationId(registrationResponse.data.subscription_id);
        trackBeginRegistration(event?.name ?? '', total);

        const paymentResponse = await eventApi.processPayment(registrationResponse.data.subscription_id, paymentMethod);

        if (paymentResponse?.redirect_url) {
          // PayPal case - redirect to PayPal
          storePendingConversion({
            value: paidValue,
            eventName: event?.name ?? '',
            config: event?.tracking_config ?? null,
            scripts: event?.tracking_scripts_list ?? [],
          });
          window.location.href = paymentResponse.redirect_url;
        } else if (paymentResponse?.bank_details) {
          // Bonifico case - show bank details
          setBankDetails(paymentResponse.bank_details);
          setBankDetailsDialogOpen(true);
        } else if (paymentResponse?.payment_method === 'carta_docente') {
          // Carta del Docente: istruzioni via email, conferma manuale
          setCartaDocenteDialogOpen(true);
        } else {
          setError(t('evento.err_risposta', 'Risposta di pagamento non valida dal server'));
        }
      } else {
        // Iscrizione senza pagamento online (gratuita): conversione completata subito
        trackConversion(total);
        setSuccess(true);
      }
    } catch (err: any) {
      console.error('Error submitting form:', err);
      setError(err.response?.data?.message || t('evento.err_invio', "Errore nell'invio del form"));
    } finally {
      setSubmitting(false);
      setPaymentDialogOpen(false);
      setConfirmDialogOpen(false);
    }
  };

  const remapKeysToField = (dataToSubmit: FormData): FormData => {
    const result: FormData = {};
    let prefix = 'field_';
    for (const key in dataToSubmit) {

      if (/^\d+$/.test(key)) {
        const newKey = `${prefix}${key}`;
        result[newKey] = dataToSubmit[key];
      } else {
        // Altrimenti manteniamo la chiave originale
        result[key] = dataToSubmit[key];
      }
    }

    return result;
  };
  const handlePaymentMethodSelect = (method: string) => {
    if (!pendingFormData) return;

    const priceId = pendingFormData.price_id as number | undefined;
    const plan = findActivePlan(priceId, method);
    // Le rate ripartiscono il totale comprensivo di supplementi (il server
    // rifiuta un piano la cui somma non coincide con l'importo dovuto)
    const total = computeOrder(pendingFormData).total;

    if (plan && total > 0 && !isGroupPending()) {
      setActivePlan(plan);
      setActiveMethod(method);
      setActiveTotal(total);
      setPaymentDialogOpen(false);
      setInstalmentsDialogOpen(true);
      return;
    }

    submitForm(pendingFormData, method);
  };

  const handleInstalmentsConfirm = (instalments: InstalmentInput[]) => {
    if (!pendingFormData) return;
    setInstalmentsDialogOpen(false);
    submitForm({ ...pendingFormData, instalments }, activeMethod);
  };

  const handleConfirmRegistration = () => {
    if (pendingFormData) {
      submitForm(pendingFormData);
    }
  };

  const handleDialogClose = () => {
    setPaymentDialogOpen(false);
    setConfirmDialogOpen(false);
    setBankDetailsDialogOpen(false);
    setInstalmentsDialogOpen(false);
    setPendingFormData(null);
    setSelectedPaymentMethod(null);
    setActivePlan(null);
    setActiveMethod('');
    setActiveTotal(0);
    setSubmitting(false);
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !event) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="success">
          {t('evento.successo', 'Registrazione completata con successo! Ti contatteremo presto con ulteriori dettagli.')}
        </Alert>
      </Container>
    );
  }

  const getBackgroundImageUrl = () => {
    if (!event?.background_image_path) return null;
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:9091/api';
    return `${apiBaseUrl}/stream/public/${event.background_image_path}`;
  };

  const backgroundImageUrl = getBackgroundImageUrl();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        scrollbarGutter: 'stable',
        backgroundImage: backgroundImageUrl ? `url(${backgroundImageUrl})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        position: 'relative',
        '&::before': backgroundImageUrl ? {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          zIndex: 1,
        } : {},
      }}
    >
      <Container 
        maxWidth="md" 
        sx={{ 
          py: 4, 
          position: 'relative', 
          zIndex: 2 
        }}
      >
        {!!(event as any)?.multilingual_enabled && (
          <LanguageSelector sx={{ mb: 1.5, maxWidth: 720, mx: 'auto' }} />
        )}
        {event && (
          <DynamicForm
            event={event}
            onSubmit={handleFormSubmit}
            loading={submitting}
            error={error}
          />
        )}
      </Container>

      {/* Group Participants Dialog */}
      <GroupParticipantsDialog
        open={groupDialogOpen}
        groupSize={groupSizeOf(pendingFormData)}
        unitAmount={computeTotalForPrice(pendingFormData?.price_id as number | undefined)}
        referenteAmount={(participates) => {
          const base = participates ? computeTotalForPrice(pendingFormData?.price_id as number | undefined) : 0;
          return personTotal(base, computeSupplements(event, (pendingFormData ?? {}) as any, pendingFormData?.is_ente === true, false, base));
        }}
        participantAmount={(lookups) => {
          const unit = computeTotalForPrice(pendingFormData?.price_id as number | undefined);
          return personTotal(unit, computeSupplements(event, lookups ?? {}, false, true, unit));
        }}
        priceName={event?.prices?.find(p => p.id === pendingFormData?.price_id)?.name ?? undefined}
        referenteEmail={String(pendingFormData?.customer_email ?? '')}
        participantLookups={event?.lookups?.filter(l => l.participant_scope === 'both' || l.participant_scope === 'participants') ?? []}
        initial={pendingFormData?.participants as GroupParticipant[] | undefined}
        initialReferenteParticipates={pendingFormData?.referente_participates !== false}
        onClose={() => setGroupDialogOpen(false)}
        onConfirm={handleGroupParticipantsConfirm}
      />

      {/* Payment Method Selection Dialog */}
      <Dialog
        open={paymentDialogOpen}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('pagamento.titolo', 'Seleziona il metodo di pagamento')}</DialogTitle>
        <DialogContent>
          {(() => {
            const order = computeOrder(pendingFormData);
            if (!order.referenteSupplements.length && !order.participantSupplements.some(l => l.length)) return null;
            const priceName = event?.prices?.find(p => p.id === pendingFormData?.price_id)?.name ?? t('gruppo.iscrizione', 'Iscrizione');
            return (
              <Box sx={{ p: 2, mb: 2, backgroundColor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('riepilogo.titolo', 'Riepilogo importo')}</Typography>
                {order.persons > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">
                      {priceName}{order.persons > 1 ? ` × ${order.persons}` : ''}
                    </Typography>
                    <Typography variant="body2">{fmtMoney(order.unit * order.persons)}</Typography>
                  </Box>
                )}
                {order.referenteSupplements.map(sup => (
                  <Box key={`r-${sup.lookupId}`} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">{sup.name}</Typography>
                    <Typography variant="body2">{signedMoney(sup.amount, fmtMoney)}</Typography>
                  </Box>
                ))}
                {order.participantSupplements.some(l => l.length) && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">{t('riepilogo.supp_partecipanti', 'Supplementi e riduzioni dei partecipanti')}</Typography>
                    <Typography variant="body2">
                      {signedMoney(order.participantSupplements.reduce((a, l) => a + sumSupplements(l), 0), fmtMoney)}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="subtitle2">{t('gruppo.totale', 'Totale')}</Typography>
                  <Typography variant="subtitle2">{fmtMoney(order.total)}</Typography>
                </Box>
              </Box>
            );
          })()}
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('pagamento.sottotitolo', 'Scegli come desideri effettuare il pagamento per la registrazione:')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {event?.bank_account_id && (
              <Card
                variant="outlined"
                sx={{
                  border: 2,
                  borderColor: selectedPaymentMethod === 'bonifico' ? 'primary.main' : 'divider',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  ...(selectedPaymentMethod === 'bonifico' && {
                    boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
                  }),
                }}
              >
                <CardActionArea onClick={() => setSelectedPaymentMethod('bonifico')}>
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                    <Radio checked={selectedPaymentMethod === 'bonifico'} tabIndex={-1} />
                    <AccountBalanceIcon sx={{ fontSize: 36, color: '#455a64' }} />
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>{t('pagamento.bonifico', 'Bonifico Bancario')}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('pagamento.bonifico_desc', 'Pagamento tramite bonifico bancario')}
                      </Typography>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            )}
            {event?.paypal_store_id && (
              <Card
                variant="outlined"
                sx={{
                  border: 2,
                  borderColor: selectedPaymentMethod === 'paypal' ? 'primary.main' : 'divider',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  ...(selectedPaymentMethod === 'paypal' && {
                    boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
                  }),
                }}
              >
                <CardActionArea onClick={() => setSelectedPaymentMethod('paypal')}>
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                    <Radio checked={selectedPaymentMethod === 'paypal'} tabIndex={-1} />
                    <Box sx={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32">
                        <path fill="#003087" d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797H9.603c-.564 0-1.04.413-1.127.974L7.076 21.337z"/>
                        <path fill="#0070E0" d="M18.171 7.394c-.98 4.876-4.26 6.573-8.467 6.573H8.06L6.476 23.22a.453.453 0 0 0 .447.525h3.143c.468 0 .865-.34.938-.803l.039-.2.744-4.716.048-.258a.946.946 0 0 1 .938-.803h.59c3.828 0 6.823-1.555 7.7-6.054.367-1.88.177-3.45-.793-4.517z"/>
                      </svg>
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>PayPal</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('pagamento.paypal_desc', 'Pagamento sicuro con PayPal')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('pagamento.paypal_rate', 'Paga in 3 rate senza interessi - o con 6, 12, 24 rate con interessi')}
                      </Typography>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            )}
            {event?.stripe_store_id && (
              <Card
                variant="outlined"
                sx={{
                  border: 2,
                  borderColor: selectedPaymentMethod === 'stripe' ? 'primary.main' : 'divider',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  ...(selectedPaymentMethod === 'stripe' && {
                    boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
                  }),
                }}
              >
                <CardActionArea onClick={() => setSelectedPaymentMethod('stripe')}>
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                    <Radio checked={selectedPaymentMethod === 'stripe'} tabIndex={-1} />
                    <Box sx={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32">
                        <path fill="#635BFF" d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.918 3.757 7.076c0 4.72 2.878 6.012 6.357 7.326 2.172.831 2.878 1.426 2.878 2.348 0 .947-.769 1.52-2.172 1.52-1.901 0-4.812-.89-6.89-2.082L3 21.683C4.941 22.863 7.768 24 11.263 24c2.636 0 4.812-.652 6.357-1.872 1.603-1.305 2.38-3.147 2.38-5.365 0-4.927-2.924-6.146-6.024-7.613z"/>
                      </svg>
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>Stripe</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('pagamento.stripe_desc', 'Carta di credito, Google Pay, Klarna e altro')}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        {/* Klarna */}
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#F4B6C7', width: 68, height: 36 }}>
                          <svg viewBox="4 14 50 13" width="52" height="18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" clipRule="evenodd" d="M7.413 25.796H5V15h2.413v10.796zm6.027-10.796h-2.362c0 1.987-.89 3.81-2.441 5.003l-.936.72 3.625 5.073h2.98l-3.335-4.67c1.581-1.616 2.468-3.79 2.468-6.126zm3.85 10.793H15.01V15.002h2.279v10.791zm6.887-7.465v.478a3.69 3.69 0 00-2.158-.684c-2.12 0-3.837 1.764-3.837 3.939s1.718 3.939 3.837 3.939a3.69 3.69 0 002.158-.684v.477h2.177v-7.465h-2.177zm-.007 3.733c0 1.06-.883 1.921-1.973 1.921s-1.973-.86-1.973-1.921.883-1.92 1.973-1.92 1.973.86 1.973 1.92zm22.976-3.255v-.478h2.177v7.465h-2.177v-.477a3.69 3.69 0 01-2.158.684c-2.12 0-3.837-1.764-3.837-3.939s1.718-3.939 3.837-3.939c.801 0 1.543.253 2.158.684zm-1.98 5.176c1.09 0 1.973-.86 1.973-1.921s-.883-1.92-1.973-1.92-1.973.86-1.973 1.92.883 1.921 1.973 1.921zm5.102.566c0-.774.612-1.402 1.366-1.402.755 0 1.366.628 1.366 1.402 0 .775-.611 1.403-1.366 1.403-.754 0-1.366-.628-1.366-1.403zm-12.986-6.42c-.87 0-1.693.277-2.243 1.042v-.84h-2.168v7.464h2.195v-3.923c0-1.135.741-1.69 1.634-1.69.957 0 1.507.586 1.507 1.675v3.938h2.175v-4.747c0-1.737-1.346-2.919-3.1-2.919zm-7.56.2v.973c.437-.584 1.25-.972 2.134-.972v2.172h-.007l-.004-.001-.006-.001-.008-.001c-.862 0-2.103.632-2.103 1.808v3.485h-2.234v-7.464h2.228z" fill="#17120F"/>
                          </svg>
                        </Box>
                        {/* Satispay */}
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fff', width: 68, height: 36 }}>
                          <img
                            src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/a5/Logo_di_Satispay.svg/250px-Logo_di_Satispay.svg.png"
                            alt="Satispay"
                            style={{ maxHeight: 20, maxWidth: 56, objectFit: 'contain' }}
                          />
                        </Box>
                        {/* Carta di credito */}
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fff', width: 68, height: 36, gap: 0.5 }}>
                          <svg viewBox="0 0 24 24" width="18" height="18" xmlns="http://www.w3.org/2000/svg">
                            <path fill="#1565C0" d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/>
                          </svg>
                          <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#424242', lineHeight: 1, whiteSpace: 'nowrap' }}>Carta</Typography>
                        </Box>
                        {/* Google Pay */}
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fff', width: 68, height: 36 }}>
                          <svg viewBox="3 8 60 30" width="56" height="28" preserveAspectRatio="xMidYMid meet" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" clipRule="evenodd" d="M33.06 31.516V25.474h3.119c1.277 0 2.356-.428 3.235-1.272l.211-.215c1.606-1.748 1.5-4.471-.211-6.089-.856-.856-2.029-1.32-3.236-1.296h-5.006v14.914h1.888zm.001-7.874V18.433h3.166c.68 0 1.325.262 1.806.737 1.02.999 1.043 2.664.058 3.699-.48.512-1.16.797-1.864.773h-3.166zM48.43 22.108c-.81-.749-1.911-1.13-3.306-1.13-1.794 0-3.142.666-4.033 1.987l1.665 1.058c.61-.904 1.443-1.356 2.498-1.356.668 0 1.313.25 1.817.702.492.428.774 1.046.774 1.7v.44c-.727-.404-1.641-.618-2.767-.618-1.313 0-2.368.309-3.154.94-.785.63-1.184 1.463-1.184 2.521-.023.964.387 1.88 1.114 2.498.738.666 1.676.999 2.778.999 1.301 0 2.333-.583 3.118-1.749h.082v1.416h1.806V25.224c0-1.32-.399-2.367-1.208-3.116zm-5.123 7.529c-.387-.286-.621-.749-.621-1.249 0-.559.258-1.023.762-1.392.516-.368 1.161-.558 1.923-.558 1.056-.012 1.876.226 2.463.701 0 .809-.317 1.51-.938 2.106-.563.57-1.325.892-2.123.892-.528.012-1.044-.166-1.466-.5zm10.387 6.363L60 21.311h-2.052l-2.919 7.327h-.035l-2.99-7.327h-2.051l4.138 9.55-2.345 5.138h1.946z" fill="#3C4043"/>
                            <path d="M26.544 24.166c0-.583-.047-1.166-.14-1.737h-7.96v3.295h4.559c-.187 1.058-.795 2.01-1.687 2.604v2.141h2.72c1.594-1.487 2.508-3.687 2.508-6.303z" fill="#4285F4"/>
                            <path d="M18.444 32.539c2.274 0 4.197-.761 5.592-2.07l-2.72-2.14c-.776.523-1.749.82-2.886.82-2.204 0-4.068-1.51-4.736-3.532l-2.802 2.212c1.43 2.89 4.35 4.71 7.552 4.71z" fill="#34A853"/>
                            <path d="M13.708 25.617c-.352-1.059-.352-2.212 0-3.283l-2.802-2.2c-1.208 2.414-1.208 5.269 0 7.683l2.802-2.2z" fill="#FBBC04"/>
                            <path d="M18.444 18.802c1.208-.024 2.368.44 3.236 1.284l2.415-2.45c-1.536-1.45-3.552-2.248-5.65-2.224-3.19 0-6.109 1.832-7.539 4.722l2.802 2.212c.668-2.034 2.532-3.544 4.736-3.544z" fill="#EA4335"/>
                          </svg>
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            )}
            {!!event?.carta_docente_enabled && !isGroupPending() && (
              <Card
                variant="outlined"
                sx={{
                  border: 2,
                  borderColor: selectedPaymentMethod === 'carta_docente' ? 'primary.main' : 'divider',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  ...(selectedPaymentMethod === 'carta_docente' && {
                    boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
                  }),
                  ...(selectedPriceHasActiveDiscount() && { opacity: 0.55 }),
                }}
              >
                <CardActionArea
                  disabled={selectedPriceHasActiveDiscount()}
                  onClick={() => setSelectedPaymentMethod('carta_docente')}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                    <Radio
                      checked={selectedPaymentMethod === 'carta_docente'}
                      disabled={selectedPriceHasActiveDiscount()}
                      tabIndex={-1}
                    />
                    <SchoolIcon sx={{ fontSize: 36, color: '#00695c' }} />
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>{t('pagamento.cd', 'Carta del Docente')}</Typography>
                      {selectedPriceHasActiveDiscount() ? (
                        <Typography variant="body2" color="text.secondary" fontWeight={500}>
                          {t('pagamento.cd_sconto', 'Disponibile solo per iscrizioni a prezzo pieno: non utilizzabile con lo sconto attivo')}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {t('pagamento.cd_desc', 'Pagamento con buono Carta del Docente: riceverai le istruzioni via email')}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 1 }}>
          <Button variant="outlined" onClick={handleDialogClose}>
            {t('common.annulla', 'Annulla')}
          </Button>
          <Button
            variant="contained"
            disabled={!selectedPaymentMethod}
            onClick={() => selectedPaymentMethod && handlePaymentMethodSelect(selectedPaymentMethod)}
          >
            {t('pagamento.procedi', 'Procedi al pagamento')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Free Registration Confirmation Dialog */}
      <Dialog 
        open={confirmDialogOpen} 
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('gratuita.titolo', 'Conferma registrazione')}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('gratuita.testo', "Stai per completare la registrazione gratuita all'evento. Vuoi procedere?")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button 
            variant="contained" 
            onClick={handleConfirmRegistration}
            size="large"
          >
            {t('gratuita.conferma', 'Conferma registrazione')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Carta del Docente Dialog */}
      <Dialog
        open={cartaDocenteDialogOpen}
        onClose={() => {
          setCartaDocenteDialogOpen(false);
          setSuccess(true);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('cd.titolo', 'Pagamento con Carta del Docente')}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('cd.testo', "La tua iscrizione è stata registrata. Ti abbiamo inviato un'email con le istruzioni per completare il pagamento con la Carta del Docente.")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('cd.verifica', "L'iscrizione sarà confermata dalla segreteria dopo la verifica del buono.")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            variant="contained"
            onClick={() => {
              setCartaDocenteDialogOpen(false);
              setSuccess(true);
            }}
          >
            {t('common.preso_nota', 'Ho preso nota')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bank Details Dialog */}
      <Dialog 
        open={bankDetailsDialogOpen} 
        onClose={() => {
          setBankDetailsDialogOpen(false);
          setSuccess(true);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('bonifico.titolo', 'Dettagli per il Bonifico')}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('bonifico.testo', 'Per completare la registrazione, effettua un bonifico con i seguenti dati:')}
          </Typography>
          {bankDetails && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.100', borderRadius: 1 }}>
              {bankDetails.beneficiary && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>{t('bonifico.beneficiario', 'Beneficiario')}:</strong> {bankDetails.beneficiary}
                </Typography>
              )}
              {bankDetails.iban && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>{t('bonifico.iban', 'IBAN')}:</strong> {bankDetails.iban}
                </Typography>
              )}
              {bankDetails.bic && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>BIC/SWIFT:</strong> {bankDetails.bic}
                </Typography>
              )}
              {bankDetails.bank_name && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>{t('bonifico.banca', 'Banca')}:</strong> {bankDetails.bank_name}
                </Typography>
              )}
              {bankDetails.causale && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>{t('bonifico.causale', 'Causale')}:</strong> {bankDetails.causale}
                </Typography>
              )}
              {bankDetails.amount && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>{t('bonifico.importo', 'Importo')}:</strong> {bankDetails.amount}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            variant="contained"
            onClick={() => {
              setBankDetailsDialogOpen(false);
              setSuccess(true);
            }}
            size="large"
          >
            {t('common.preso_nota', 'Ho preso nota')}
          </Button>
        </DialogActions>
      </Dialog>

      {activePlan && (
        <InstalmentsDialog
          open={instalmentsDialogOpen}
          onClose={handleDialogClose}
          onConfirm={handleInstalmentsConfirm}
          plan={activePlan}
          totalAmount={activeTotal}
          paymentMethod={activeMethod}
        />
      )}
    </Box>
  );
};

export default EventPage;